import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Box, ButtonBase, CircularProgress, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import StraightenIcon from '@mui/icons-material/Straighten';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Document, Page, pdfjs } from 'react-pdf';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import type { OptionProduct } from '@/types';

// Worker pdf.js servito come asset locale (Vite `?url`) — niente CDN, CSP-safe. La versione di
// pdfjs-dist è pinnata a quella usata da react-pdf per evitare il mismatch "API/Worker version".
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

interface ProductActionsProps {
  /** Prodotto di catalogo dell'opzione (dal BE). Se null/senza asset → non renderizza nulla. */
  product: OptionProduct | null;
  /** Nome leggibile dell'opzione (fallback per alt/nome file). */
  name?: string;
}

// Testi 1:1 dal legacy (ImageZoom / PdfViewer).
const IMG_UNAVAILABLE = "L'immagine al momento non è disponibile.";
const PDF_UNAVAILABLE = 'La scheda tecnica al momento non è disponibile.';
const IMG_DISCLAIMER =
  'Le immagini sono a puro scopo illustrativo, dimensioni e colori sono orientativi. Ogni elemento ' +
  'può risultare alterato a causa della taratura dello schermo, della luce e dell’angolo di visualizzazione.';

/** Scarica un asset (foto/pdf) come file reale via blob (richiede CORS sul Worker, ora presente). */
async function downloadAsset(url: string, filename: string): Promise<void> {
  try {
    const res = await fetch(url);
    if (!res.ok) return;
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objUrl);
  } catch {
    // Il download è best-effort: un fallimento (rete/CORS) non deve rompere il viewer.
  }
}

/**
 * Bottoni "Foto" e "Scheda Tecnica" della card opzione, fedeli al legacy (OptionCard + ImageZoomButton
 * → ImageZoom / PdfViewer). Righe full-width con bordo, icona + label 13px. Ogni bottone appare SOLO se
 * il rispettivo URL è presente (Foto ⇐ imageUrl, Scheda ⇐ dataSheetUrl). Foto → viewer full-screen con
 * immagine + download; Scheda → viewer full-screen con PDF reso INLINE via react-pdf (canvas pdf.js),
 * come il legacy. Il campo `techInfo` NON è renderizzato (il legacy non lo mostra). Unica libertà:
 * stato "non disponibile" se l'asset dà 404 (solo substep "Bagno con vasca", mai caricato nel legacy).
 */
export function ProductActions({ product, name }: ProductActionsProps) {
  const [photoOpen, setPhotoOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  if (!product) return null;

  const hasPhoto = product.imageUrl != null && product.imageUrl !== '';
  const hasPdf = product.dataSheetUrl != null && product.dataSheetUrl !== '';
  if (!hasPhoto && !hasPdf) return null;

  const label = product.description ?? name ?? 'Prodotto';
  const baseName = product.ainId ?? name ?? 'scheda-tecnica';

  return (
    <Box sx={{ width: '100%' }}>
      {hasPhoto && (
        <AssetRow icon={<PhotoCameraOutlinedIcon fontSize="small" />} label="Foto" onClick={() => setPhotoOpen(true)} />
      )}
      {hasPdf && (
        <AssetRow
          icon={<StraightenIcon fontSize="small" />}
          label="Scheda Tecnica"
          onClick={() => setSheetOpen(true)}
        />
      )}

      {hasPhoto && (
        <PhotoViewer
          open={photoOpen}
          onClose={() => setPhotoOpen(false)}
          src={product.imageUrl!}
          alt={label}
          downloadName={`${baseName}.jpg`}
        />
      )}
      {hasPdf && (
        <PdfViewer
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          src={product.dataSheetUrl!}
          downloadName={`${baseName}.pdf`}
        />
      )}
    </Box>
  );
}

/** Riga-bottone Foto/Scheda in stile legacy ImageZoomButton (full-width, bordo, 13px, icona a sx). */
function AssetRow({ icon, label, onClick }: { icon: ReactNode; label: string; onClick: () => void }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: '100%',
        justifyContent: 'flex-start',
        gap: 1.25,
        px: 1.5,
        height: 30,
        fontSize: 13,
        lineHeight: '16px',
        color: 'text.secondary',
        borderTop: '1px solid #C4C4C4',
        '&:last-of-type': { borderBottom: '1px solid #C4C4C4' },
      }}
    >
      {icon}
      <Box component="span">{label}</Box>
    </ButtonBase>
  );
}

/**
 * Chrome full-screen comune ai viewer (fedele a ImageZoom/PdfViewer): sfondo scuro + blur, icona di
 * chiusura (cerchio nero, X arancio) in alto a destra, e slot download (in basso a destra).
 */
function FullScreenViewer({
  open,
  onClose,
  onDownload,
  children,
}: {
  open: boolean;
  onClose: () => void;
  onDownload?: (() => void) | undefined;
  children: ReactNode;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(15px)' } } }}
    >
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, md: '30px' },
          outline: 'none',
        }}
      >
        <ButtonBase
          onClick={onClose}
          aria-label="Chiudi"
          sx={{
            position: 'absolute',
            top: 30,
            right: 30,
            width: 50,
            height: 50,
            borderRadius: '50%',
            bgcolor: '#000',
            zIndex: 10,
          }}
        >
          <CloseIcon sx={{ color: 'primary.main', fontSize: 20 }} />
        </ButtonBase>

        {children}

        {onDownload && (
          <ButtonBase
            onClick={onDownload}
            aria-label="Scarica"
            sx={{ position: 'absolute', right: 30, bottom: 30, color: '#fff' }}
          >
            <DownloadOutlinedIcon sx={{ fontSize: 44 }} />
          </ButtonBase>
        )}
      </Box>
    </Modal>
  );
}

/** Viewer Foto: immagine centrata (no zoom per singola immagine, come legacy) + download + disclaimer. */
function PhotoViewer({
  open,
  onClose,
  src,
  alt,
  downloadName,
}: {
  open: boolean;
  onClose: () => void;
  src: string;
  alt: string;
  downloadName: string;
}) {
  const [error, setError] = useState(false);
  useEffect(() => {
    if (open) setError(false);
  }, [open]);

  return (
    <FullScreenViewer
      open={open}
      onClose={onClose}
      onDownload={error ? undefined : () => void downloadAsset(src, downloadName)}
    >
      {error ? (
        <Typography sx={{ color: '#fff' }}>{IMG_UNAVAILABLE}</Typography>
      ) : (
        <>
          <Box
            component="img"
            src={src}
            alt={alt}
            onError={() => setError(true)}
            sx={{ maxWidth: '70vw', maxHeight: '70vh', objectFit: 'contain' }}
          />
          <Typography
            sx={{
              position: 'absolute',
              bottom: 40,
              width: '60%',
              textAlign: 'center',
              color: '#fff',
              fontSize: 12,
              textShadow: '0px 2px 7px #000',
            }}
          >
            {IMG_DISCLAIMER}
          </Typography>
        </>
      )}
    </FullScreenViewer>
  );
}

/** Larghezza pagina PDF ~80% viewport (come il legacy), con un tetto per non sgranare. */
function pdfPageWidth(): number {
  const w = typeof window !== 'undefined' ? window.innerWidth : 1024;
  return Math.min(Math.floor(w * 0.8), 1000);
}

/**
 * Viewer Scheda Tecnica: PDF reso INLINE con react-pdf (canvas pdf.js) nel chrome full-screen, come il
 * legacy PdfViewer: sfondo documento #f0efee, navigazione pagine (chevron + pallini) se multipagina,
 * download, e testo d'errore legacy su 404 (substep "Bagno con vasca").
 */
function PdfViewer({
  open,
  onClose,
  src,
  downloadName,
}: {
  open: boolean;
  onClose: () => void;
  src: string;
  downloadName: string;
}) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageWidth, setPageWidth] = useState<number>(() => pdfPageWidth());
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFailed(false);
    setNumPages(null);
    setPageNumber(1);
    const onResize = (): void => setPageWidth(pdfPageWidth());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, src]);

  const previousPage = useCallback(() => setPageNumber((p) => (p > 1 ? p - 1 : p)), []);
  const nextPage = useCallback(
    () => setPageNumber((p) => (numPages != null && p < numPages ? p + 1 : p)),
    [numPages],
  );

  return (
    <FullScreenViewer
      open={open}
      onClose={onClose}
      onDownload={failed ? undefined : () => void downloadAsset(src, downloadName)}
    >
      <Box
        sx={{
          position: 'relative',
          maxWidth: '80vw',
          height: '90vh',
          bgcolor: '#f0efee',
          display: 'flex',
          justifyContent: 'center',
          overflow: 'hidden auto',
          pt: '30px',
          boxShadow: '0 0 0.5rem 0 rgba(0,0,0,0.3)',
        }}
      >
        <Document
          file={src}
          loading={<CircularProgress sx={{ color: '#000', mt: 6 }} />}
          error={<Typography sx={{ color: '#000', p: 3 }}>{PDF_UNAVAILABLE}</Typography>}
          onLoadSuccess={({ numPages: n }) => {
            setNumPages(n);
            setPageNumber(1);
          }}
          onLoadError={() => setFailed(true)}
        >
          <Page
            pageNumber={pageNumber}
            width={pageWidth}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Document>

        {!failed && numPages != null && numPages > 1 && (
          <>
            <ButtonBase onClick={previousPage} aria-label="Pagina precedente" sx={pagerBtnSx('left')}>
              <ChevronLeftIcon sx={{ color: '#fff' }} />
            </ButtonBase>
            <ButtonBase onClick={nextPage} aria-label="Pagina successiva" sx={pagerBtnSx('right')}>
              <ChevronRightIcon sx={{ color: '#fff' }} />
            </ButtonBase>
            <Box
              sx={{
                position: 'absolute',
                bottom: 10,
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'center',
                gap: 0.75,
              }}
            >
              {Array.from({ length: numPages }).map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: i + 1 === pageNumber ? 'rgba(135,135,135,1)' : 'rgba(135,135,135,0.5)',
                  }}
                />
              ))}
            </Box>
          </>
        )}
      </Box>
    </FullScreenViewer>
  );
}

/** Stile dei pulsanti di navigazione pagina (chevron), come le direction-button legaci. */
function pagerBtnSx(side: 'left' | 'right') {
  return {
    position: 'absolute' as const,
    top: '50%',
    transform: 'translateY(-50%)',
    [side]: 8,
    width: 30,
    height: 30,
    bgcolor: '#676766',
    border: '1px solid #fff',
    zIndex: 2,
  };
}
